import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { useSitePage, type SitePageRecord } from "@/lib/cms";
import { buildBreadcrumb, buildWebPage } from "@/lib/seo/schemas";

interface LegalPageProps {
  slug: string;
}

const SEO_FALLBACKS: Record<string, { title: string; description: string }> = {
  privacy: {
    title: "Privacy Policy | MVIBE",
    description: "How MVIBE collects, uses and protects your personal data across our coaching, training and consulting services and the mvibeon.com website.",
  },
  terms: {
    title: "Terms of Service | MVIBE",
    description: "The terms governing your use of the MVIBE website, coaching sessions, training programmes and consulting engagements provided by MVIBE.",
  },
  cookies: {
    title: "Cookie Policy | MVIBE",
    description: "How MVIBE uses cookies and similar technologies on mvibeon.com, what they track, and how you can control or disable them in your browser.",
  },
  disclaimer: {
    title: "Disclaimer | MVIBE",
    description: "Important disclaimers regarding MVIBE coaching, training and consulting content, results, and the information published on the mvibeon.com website.",
  },
};

const LegalPage = ({ slug }: LegalPageProps) => {
  const { data } = useSitePage(slug);
  const page = data as SitePageRecord;
  const sections = page.content.sections ?? [];
  const fallback = SEO_FALLBACKS[slug] ?? { title: page.title, description: "" };

  return (
    <div>
      <SEOHead
        title={page.seo.title || fallback.title}
        description={page.seo.description || fallback.description}
        canonical={page.seo.canonical}
        type={page.seo.type}
        schema={[
          buildWebPage({
            name: page.title,
            description: page.seo.description || fallback.description,
            path: `/${slug}`,
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: page.title, path: `/${slug}` },
          ]),
        ]}
      />


      <section className="bg-primary py-14">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-2 text-base text-primary-foreground/70">
            <Link to="/" className="transition-colors hover:text-accent">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">{page.title}</span>
          </nav>
          <h1 className="text-3xl font-heading font-bold text-primary-foreground md:text-5xl">{page.title}</h1>
          {page.content.intro ? (
            <p className="mx-auto mt-4 max-w-3xl text-base leading-7 text-primary-foreground/80 md:text-lg">
              {page.content.intro}
            </p>
          ) : null}
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto max-w-4xl px-4">
          <div className="space-y-8 rounded-3xl border border-border bg-card p-6 shadow-sm md:p-8">
            {sections.map((section) => (
              <section key={section.heading} className="space-y-3">
                <h2 className="text-2xl font-heading font-bold text-foreground">{section.heading}</h2>
                <div className="space-y-3 text-base leading-8 text-muted-foreground md:text-lg">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
